import { CompletionContext, CompletionResult, Completion } from "@codemirror/autocomplete";

import { FormulaSchema, FormulaTypeSchema } from "../../model/formula";

/**
 * A resolved "scope" while walking a dotted path such as `TransactionInfo.Transaction.TypeName`.
 * - root: the top level, where root objects are valid.
 * - complex: inside a drillable type described in the schema.
 * - primitive: inside a leaf value (string/number/DateTime/bool) where only synthetic
 *   members/methods are offered.
 */
type Scope =
  | { kind: "root" }
  | { kind: "complex"; typeRef: string }
  | { kind: "primitive"; primitive: PrimitiveKind; nullable: boolean };

type PrimitiveKind = "string" | "datetime" | "number" | "bool" | "other";

/** A synthetic member offered on a primitive value, with an optional C# signature hint. */
interface PrimitiveMember {
  name: string;
  kind: "method" | "property";
  /** Parameter hint / signature shown as the completion `detail`, e.g. "(int startIndex): string". */
  signature?: string;
  /** Longer description (incl. overloads) shown in the completion info panel. */
  info?: string;
}

const STRING_MEMBERS: PrimitiveMember[] = [
  { name: "Trim", kind: "method", signature: "(): string", info: "Removes leading and trailing whitespace." },
  { name: "TrimStart", kind: "method", signature: "(): string", info: "Removes leading whitespace." },
  { name: "TrimEnd", kind: "method", signature: "(): string", info: "Removes trailing whitespace." },
  { name: "ToUpper", kind: "method", signature: "(): string", info: "Returns the string in upper case." },
  { name: "ToLower", kind: "method", signature: "(): string", info: "Returns the string in lower case." },
  {
    name: "Substring",
    kind: "method",
    signature: "(int startIndex, int length): string",
    info:
      "Returns a substring.\n" +
      "Overloads:\n" +
      "  Substring(int startIndex)\n" +
      "  Substring(int startIndex, int length)",
  },
  {
    name: "Replace",
    kind: "method",
    signature: "(string oldValue, string newValue): string",
    info: "Replaces all occurrences of oldValue with newValue.",
  },
  { name: "PadLeft", kind: "method", signature: "(int totalWidth): string", info: "Right-aligns and pads with spaces on the left." },
  { name: "PadRight", kind: "method", signature: "(int totalWidth): string", info: "Left-aligns and pads with spaces on the right." },
  { name: "StartsWith", kind: "method", signature: "(string value): bool", info: "Whether the string starts with value." },
  { name: "EndsWith", kind: "method", signature: "(string value): bool", info: "Whether the string ends with value." },
  { name: "Contains", kind: "method", signature: "(string value): bool", info: "Whether the string contains value." },
  { name: "IndexOf", kind: "method", signature: "(string value): int", info: "Zero-based index of value, or -1 if not found." },
  { name: "Length", kind: "property", signature: ": int", info: "Number of characters." },
];

const DATETIME_MEMBERS: PrimitiveMember[] = [
  { name: "Year", kind: "property", signature: ": int" },
  { name: "Month", kind: "property", signature: ": int" },
  { name: "Day", kind: "property", signature: ": int" },
  { name: "Hour", kind: "property", signature: ": int" },
  { name: "Minute", kind: "property", signature: ": int" },
  { name: "Second", kind: "property", signature: ": int" },
  { name: "DayOfWeek", kind: "property", signature: ": DayOfWeek" },
  { name: "DayOfYear", kind: "property", signature: ": int" },
  { name: "Date", kind: "property", signature: ": DateTime", info: "The date component with the time set to midnight." },
  { name: "AddDays", kind: "method", signature: "(double value): DateTime" },
  { name: "AddMonths", kind: "method", signature: "(int months): DateTime" },
  { name: "AddYears", kind: "method", signature: "(int value): DateTime" },
  { name: "ToString", kind: "method", signature: "(string format): string", info: "Formats the date, e.g. ToString(\"yyyy-MM-dd\")." },
];

const NUMBER_MEMBERS: PrimitiveMember[] = [
  { name: "ToString", kind: "method", signature: "(string format): string", info: "Formats the number, e.g. ToString(\"N2\")." },
];
const BOOL_MEMBERS: PrimitiveMember[] = [
  { name: "ToString", kind: "method", signature: "(): string" },
];

function mapPrimitiveKind(displayType: string): PrimitiveKind {
  const base = displayType.replace(/\?$/, "").trim();
  if (base === "string") return "string";
  if (base === "DateTime" || base === "DateTimeOffset") return "datetime";
  if (base === "bool") return "bool";
  if (
    base === "int" ||
    base === "long" ||
    base === "short" ||
    base === "byte" ||
    base === "decimal" ||
    base === "double" ||
    base === "float"
  ) {
    return "number";
  }
  return "other";
}

function primitiveScope(displayType: string): Scope {
  return {
    kind: "primitive",
    primitive: mapPrimitiveKind(displayType),
    nullable: /\?$/.test(displayType.trim()),
  };
}

function getType(schema: FormulaSchema, typeRef: string): FormulaTypeSchema | undefined {
  return schema.types[typeRef];
}

/**
 * Resolves the scope reached after the given already-completed path segments.
 * Returns undefined when a segment cannot be resolved (an unknown member on a complex type).
 */
function resolveScope(schema: FormulaSchema, segments: string[]): Scope | undefined {
  let scope: Scope = { kind: "root" };

  for (const segment of segments) {
    if (scope.kind === "root") {
      const root = schema.rootObjects.find((r) => r.name === segment);
      if (!root) {
        return undefined;
      }
      scope = root.isPrimitive || !root.typeRef
        ? primitiveScope(root.displayType)
        : { kind: "complex", typeRef: root.typeRef };
    } else if (scope.kind === "complex") {
      const type = getType(schema, scope.typeRef);
      const member = type?.members.find((m) => m.name === segment);
      if (!member) {
        return undefined;
      }
      scope = member.isPrimitive || !member.typeRef
        ? primitiveScope(member.displayType)
        : { kind: "complex", typeRef: member.typeRef };
    } else {
      // primitive scope: only a small set of synthetic members can be resolved for chaining.
      const next = resolvePrimitiveMember(scope, segment);
      if (!next) {
        return undefined;
      }
      scope = next;
    }
  }

  return scope;
}

function resolvePrimitiveMember(scope: Scope & { kind: "primitive" }, segment: string): Scope | undefined {
  if (scope.nullable && segment === "Value") {
    return { kind: "primitive", primitive: scope.primitive, nullable: false };
  }
  if (scope.nullable && segment === "HasValue") {
    return { kind: "primitive", primitive: "bool", nullable: false };
  }

  switch (scope.primitive) {
    case "string":
      if (segment === "Length") return { kind: "primitive", primitive: "number", nullable: false };
      if (["StartsWith", "EndsWith", "Contains"].includes(segment))
        return { kind: "primitive", primitive: "bool", nullable: false };
      if (["Trim", "TrimStart", "TrimEnd", "ToUpper", "ToLower", "Substring", "Replace", "PadLeft", "PadRight"].includes(segment))
        return { kind: "primitive", primitive: "string", nullable: false };
      if (segment === "IndexOf") return { kind: "primitive", primitive: "number", nullable: false };
      return undefined;
    case "datetime":
      if (["Year", "Month", "Day", "Hour", "Minute", "Second", "DayOfYear"].includes(segment))
        return { kind: "primitive", primitive: "number", nullable: false };
      if (["AddDays", "AddMonths", "AddYears", "Date"].includes(segment))
        return { kind: "primitive", primitive: "datetime", nullable: false };
      if (segment === "ToString") return { kind: "primitive", primitive: "string", nullable: false };
      return undefined;
    case "number":
    case "bool":
      if (segment === "ToString") return { kind: "primitive", primitive: "string", nullable: false };
      return undefined;
    default:
      return undefined;
  }
}

function scopeCompletions(schema: FormulaSchema, scope: Scope): Completion[] {
  if (scope.kind === "root") {
    return schema.rootObjects.map((r) => ({
      label: r.name,
      type: "variable",
      detail: r.displayType,
    }));
  }
  if (scope.kind === "complex") {
    const type = getType(schema, scope.typeRef);
    if (!type) {
      return [];
    }
    return type.members.map((m) => ({
      label: m.name,
      type: m.isPrimitive ? "property" : "class",
      detail: m.displayType,
    }));
  }

  // primitive
  const members = primitiveMembers(scope.primitive);
  const completions: Completion[] = members.map((member) => primitiveCompletion(member));
  if (scope.nullable) {
    completions.unshift(
      { label: "Value", type: "property", detail: ": T" },
      { label: "HasValue", type: "property", detail: ": bool" },
    );
  }
  return completions;
}

/** Inserts `name()` and places the caret between the parentheses so arguments can be typed. */
const insertMethodCall: NonNullable<Completion["apply"]> = (view, completion, from, to) => {
  const name = completion.label;
  const insert = `${name}()`;
  view.dispatch({
    changes: { from, to, insert },
    selection: { anchor: from + name.length + 1 },
  });
};

function primitiveCompletion(member: PrimitiveMember): Completion {
  if (member.kind === "method") {
    return {
      label: member.name,
      type: "method",
      detail: member.signature,
      info: member.info,
      apply: insertMethodCall,
    };
  }
  return {
    label: member.name,
    type: "property",
    detail: member.signature,
    info: member.info,
  };
}

function primitiveMembers(primitive: PrimitiveKind): PrimitiveMember[] {
  switch (primitive) {
    case "string":
      return STRING_MEMBERS;
    case "datetime":
      return DATETIME_MEMBERS;
    case "number":
      return NUMBER_MEMBERS;
    case "bool":
      return BOOL_MEMBERS;
    default:
      return [];
  }
}

const IDENTIFIER_CHAR = /[A-Za-z0-9_]/;
const WHITESPACE = /\s/;

interface ParsedPath {
  /** Names of the fully-typed segments before the current partial (method calls stripped). */
  completed: string[];
  /** The identifier fragment currently being typed (empty when the path ends with a dot). */
  partial: string;
  /** True when the cursor is in a member-access position (a '.' precedes the partial). */
  isMemberAccess: boolean;
}

/**
 * Scans backwards from the end of {@link textBefore} to isolate the member-access chain at the
 * cursor. Unlike a flat identifier regex, this understands method-call parentheses (e.g. `Trim()`,
 * `Substring(0, 3)`, nested calls) so completion keeps working after a call — the call's arguments
 * are skipped and the method name is kept as a chain segment.
 */
function parseTrailingPath(textBefore: string): ParsedPath | null {
  let i = textBefore.length;

  const skipWhitespace = () => {
    while (i > 0 && WHITESPACE.test(textBefore[i - 1])) {
      i--;
    }
  };
  const readIdentifier = (): string => {
    const end = i;
    while (i > 0 && IDENTIFIER_CHAR.test(textBefore[i - 1])) {
      i--;
    }
    return textBefore.slice(i, end);
  };
  /** When positioned just after a ')', skip back over the balanced call arguments. */
  const skipCallArgs = (): boolean => {
    if (i === 0 || textBefore[i - 1] !== ")") {
      return false;
    }
    let depth = 0;
    while (i > 0) {
      const ch = textBefore[i - 1];
      i--;
      if (ch === ")") {
        depth++;
      } else if (ch === "(") {
        depth--;
        if (depth === 0) {
          return true;
        }
      }
    }
    return false; // unbalanced
  };

  // 1. The partial being typed (or empty when the path ends with a dot).
  skipWhitespace();
  const partial = readIdentifier();

  skipWhitespace();
  if (i === 0 || textBefore[i - 1] !== ".") {
    // Top level: not a member access, just a (possibly empty) root-object fragment.
    return { completed: [], partial, isMemberAccess: false };
  }
  i--; // consume the '.'

  // 2. Walk the preceding `segment (. segment)*` chain, where each segment may be a call.
  const completed: string[] = [];
  for (;;) {
    skipWhitespace();
    if (!skipCallArgs()) {
      if (i > 0 && textBefore[i - 1] === ")") {
        return null; // unbalanced parentheses
      }
    }
    skipWhitespace();
    const name = readIdentifier();
    if (!name) {
      return null; // e.g. a literal or operator — not a resolvable member chain
    }
    completed.unshift(name);
    skipWhitespace();
    if (i > 0 && textBefore[i - 1] === ".") {
      i--; // consume '.' and continue up the chain
      continue;
    }
    break;
  }

  return { completed, partial, isMemberAccess: true };
}

/**
 * Builds a CodeMirror completion source that offers root objects at the top level and drills into
 * member structures described by the schema.
 */
export function createFormulaCompletionSource(schema: FormulaSchema) {
  return (context: CompletionContext): CompletionResult | null => {
    const line = context.state.doc.lineAt(context.pos);
    const textBefore = line.text.slice(0, context.pos - line.from);

    const parsed = parseTrailingPath(textBefore);
    if (!parsed) {
      if (context.explicit) {
        const options = scopeCompletions(schema, { kind: "root" });
        return options.length ? { from: context.pos, options } : null;
      }
      return null;
    }

    // Top-level fragment (no leading dot): complete root objects.
    if (!parsed.isMemberAccess) {
      if (parsed.partial.length === 0 && !context.explicit) {
        return null;
      }
      const options = scopeCompletions(schema, { kind: "root" });
      return {
        from: context.pos - parsed.partial.length,
        options,
        validFor: /^[A-Za-z0-9_]*$/,
      };
    }

    const scope = resolveScope(schema, parsed.completed);
    if (!scope) {
      return null;
    }

    const options = scopeCompletions(schema, scope);
    if (!options.length) {
      return null;
    }

    return {
      from: context.pos - parsed.partial.length,
      options,
      validFor: /^[A-Za-z0-9_]*$/,
    };
  };
}

export type StructuralIssue = {
  from: number;
  to: number;
  message: string;
};

const IDENTIFIER_CHAIN = /[A-Za-z_][A-Za-z0-9_]*(?:\s*\.\s*[A-Za-z_][A-Za-z0-9_]*)*/g;

/**
 * Lightweight, false-positive-averse structural check: walks dotted chains that start with a known
 * root object and flags the first segment that is not a member of the (complex) type reached so
 * far. Navigation stops at primitives (methods/`Value` etc. are not verified here — the server
 * validation is authoritative for those).
 */
export function findStructuralIssues(schema: FormulaSchema, text: string): StructuralIssue[] {
  const issues: StructuralIssue[] = [];
  const rootNames = new Set(schema.rootObjects.map((r) => r.name));

  let chainMatch: RegExpExecArray | null;
  IDENTIFIER_CHAIN.lastIndex = 0;
  while ((chainMatch = IDENTIFIER_CHAIN.exec(text)) !== null) {
    const chain = chainMatch[0];
    if (!chain.includes(".")) {
      continue;
    }

    // Compute per-segment offsets within the original text.
    const segments = splitWithOffsets(chain, chainMatch.index);
    if (!rootNames.has(segments[0].value)) {
      continue;
    }

    let scope: Scope | undefined = { kind: "root" };
    for (const segment of segments) {
      if (!scope) {
        break;
      }
      if (scope.kind === "root") {
        const root = schema.rootObjects.find((r) => r.name === segment.value);
        if (!root) {
          scope = undefined;
        } else if (root.isPrimitive || !root.typeRef) {
          // Primitive root (e.g. DateTime): stop structural checking for this chain.
          scope = primitiveScope(root.displayType);
        } else {
          scope = { kind: "complex", typeRef: root.typeRef };
        }
      } else if (scope.kind === "complex") {
        const type = getType(schema, scope.typeRef);
        const member = type?.members.find((m) => m.name === segment.value);
        if (!member) {
          issues.push({
            from: segment.from,
            to: segment.to,
            message: `'${segment.value}' is not a member of '${type?.name ?? scope.typeRef}'.`,
          });
          scope = undefined;
          break;
        }
        scope = member.isPrimitive || !member.typeRef
          ? primitiveScope(member.displayType)
          : { kind: "complex", typeRef: member.typeRef };
      } else {
        // Reached a primitive; stop structural checking for this chain.
        break;
      }
    }
  }

  return issues;
}

function splitWithOffsets(chain: string, baseOffset: number): { value: string; from: number; to: number }[] {
  const result: { value: string; from: number; to: number }[] = [];
  const partRegex = /[A-Za-z_][A-Za-z0-9_]*/g;
  let m: RegExpExecArray | null;
  while ((m = partRegex.exec(chain)) !== null) {
    result.push({
      value: m[0],
      from: baseOffset + m.index,
      to: baseOffset + m.index + m[0].length,
    });
  }
  return result;
}
